<template>
  <div class="authentication-wrapper">
    <Header />
    <div class="login-screen flex justify-content-center align-items-center">
      <Card class="login-card">
        <template #content>
          <div class="text-center">
            <ProgressSpinner style="width: 60px; height: 60px" strokeWidth="4" />
            <p class="loading-text mt-4">Logging you in...</p>
            <p class="sub-text">Please wait while we authenticate your account</p>
          </div>
        </template>
      </Card>
    </div>
    <Footer v-if="!isMD" />
  </div>
</template>

<script>
import { API, TECHLEND_BE_URL } from '@/constants.api';
import { _get } from '@/services/crudService';
import { mapActions } from 'vuex';
import Card from 'primevue/card';
import Footer from '../Login/Footer.vue';
import Header from '../Login/Header.vue';
import ProgressSpinner from 'primevue/progressspinner';
import ScreenSize from '@/mixins/screensize.mixin';
import axios from 'axios';

export default {
  name: 'ExternalLogin',
  mixins: [ScreenSize],
  components: {
    Card,
    Header,
    Footer,
    ProgressSpinner,
  },

  async mounted() {
    await this.handleExternalLogin();
  },

  methods: {
    ...mapActions(['setLogin']),

    async handleExternalLogin() {
      try {
        // Extract token from URL query params
        const oneTimeToken = this.$route.query.token;

        if (!oneTimeToken) {
          await this.showErrorAndRedirect('No authentication token provided');
          return;
        }

        // Exchange one-time token for full JWT (unauthenticated call)
        const { data } = await axios.post(TECHLEND_BE_URL + '/external/partner-authenticate/exchange-one-time-token', {
          token: oneTimeToken,
        });

        if (!data?.token) {
          await this.showErrorAndRedirect('Invalid authentication response');
          return;
        }

        // Store the full JWT token using the same method as OTP login
        await this.setLogin(data.token);

        // Extract reference numbers from the exchange response
        const draftRefNumber = data.draftRefNumber;
        const scenarioRefNumber = data.scenarioRefNumber;
        const loanRefNumber = data.loanRefNumber;

        // Fetch current user to set user type (now authenticated)
        const { data: brokerData } = await _get(TECHLEND_BE_URL + API.USER.CURRENT_USER);
        this.$store.commit('setUserType', brokerData?.broker?.userType ?? 'broker');
        this.$store.commit('setBrokerType', data?.type ?? 'broker');

        // Show success toast
        await this.$toast.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Automatic login successful',
          life: 3000,
        });

        // Redirect to application page with appropriate query param based on what's available
        if (draftRefNumber) {
          this.$router.push({ path: '/application', query: { draftId: draftRefNumber } });
        } else if (scenarioRefNumber) {
          this.$router.push({ path: '/application', query: { scenarioId: scenarioRefNumber } });
        } else if (loanRefNumber) {
          this.$router.push({ path: '/application', query: { loanId: loanRefNumber } });
        } else {
          this.$router.push('/application');
        }
      } catch (err) {
        console.error('External login error:', err);
        const errorMessage =
          err?.response?.data?.message || err?.message || 'Automatic login failed. Please login manually.';
        await this.showErrorAndRedirect(errorMessage);
      }
    },

    async showErrorAndRedirect(errorMessage) {
      await this.$toast.add({
        severity: 'error',
        summary: 'Login Failed',
        detail: errorMessage,
        life: 5000,
      });

      // Redirect to login page after a short delay
      setTimeout(() => {
        this.$router.push('/login');
      }, 1000);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/global.scss';

.login-screen {
  width: 100%;
  margin: 2rem auto;
}

.authentication-wrapper {
  flex-direction: column;
  height: 100vh;
  display: flex;
  background: linear-gradient(180deg, #eef4fa 0%, #ffffff 100%);

  @media (min-width: 992px) {
    justify-content: space-between;
  }
}

.p-card {
  color: $primary-text-color !important;
  font-family: $font-family !important;
  width: 408px;
}

:deep(.p-card-body) {
  padding: 2.5rem 2.5rem 2rem 2.5rem !important;
}

:deep(.p-card-content) {
  font-size: 14px !important;
  color: $secondary-color !important;
  font-weight: 500;
  padding: 0 !important;
}

:deep(.p-progress-spinner-circle) {
  stroke: $primary-color !important;
}

.loading-text {
  font-size: 24px;
  margin: 0;
  font-family: Gordita-bold;
  color: $secondary-color;
}

.sub-text {
  font-size: 14px;
  margin: 0.5rem 0 0 0;
  font-family: HK-medium;
  color: rgba(2, 0, 56, 0.6);
}
</style>
